"use client";

import { useQuery } from "@tanstack/react-query";
import log from "logbeacon";
import { dayjs } from "utils";
import type { IXAxis } from "@/src/components/createChart";
import { QUERY_KEYS } from "@/src/config";
import { useOspApiClientProvider } from "@/src/hooks/useOspApiClientProvider";

const HOURS_BEFORE = 12;
const HOURS_AFTER = 12;
const TOTAL_HOURS = 24;
const MS_12H = HOURS_AFTER * 60 * 60 * 1000;
const MS_24H = TOTAL_HOURS * 60 * 60 * 1000;
/** 请求范围：最多向前 24h（用于发生后不足时往前补齐）、向后 12h */
const MS_BEFORE_MAX = HOURS_BEFORE * 2 * 60 * 60 * 1000; // 24h before

/** 资产类型：美股 / 加密货币，用于选择对应 openData 接口 */
export type TASignalPriceChartAssetType = "stock" | "crypto";

export interface UseTASignalPriceChartParams {
  /** 资产 id（stock id 或 token id） */
  assetId: string | undefined;
  /** 信号时间戳（毫秒） */
  signalTime: number | undefined;
  /** 资产类型：stock 调 getStocks+sparkline，crypto 调 getTokens+ohlc */
  assetType: TASignalPriceChartAssetType;
  enabled?: boolean;
}

export interface TASignalPriceChartData {
  XAxis: IXAxis;
  YData: number[];
}

/**
 * 取 24 小时展示窗口：优先 [signalTime-12h, signalTime+12h]；若发生后 12h 不足则往前补齐，保证总长 24h 且包含信号点
 */
function sliceTo24hWindow<T extends { timestamp?: number; time?: number }>(
  points: T[],
  signalTime: number,
): T[] {
  if (points.length === 0) {
    return [];
  }
  const getTs = (p: T) => p.timestamp ?? (p as { time?: number }).time ?? 0;
  const lastTs = Math.max(...points.map(getTs));
  const idealEnd = signalTime + MS_12H;
  const endDisplay = Math.max(signalTime, Math.min(idealEnd, lastTs));
  const startDisplay = endDisplay - MS_24H;
  return points.filter((p) => {
    const ts = getTs(p);
    return ts >= startDisplay && ts <= endDisplay;
  });
}

/**
 * 获取 TA 信号点前后共 24 小时的价格数据（前 12h + 后 12h，发生后不足则往前补齐），用于 createDualYAxisLineChart
 */
export function useTASignalPriceChart({
  assetId,
  signalTime,
  assetType,
  enabled = true,
}: UseTASignalPriceChartParams) {
  const { openDataApi } = useOspApiClientProvider();

  /** 请求窗口：前 24h 后 12h，以便发生后不足时往前补齐仍能凑够 24h */
  const startTime = signalTime ? signalTime - MS_BEFORE_MAX : undefined;
  const endTime = signalTime ? signalTime + MS_12H : undefined;

  const { data, isLoading } = useQuery({
    queryKey: [
      QUERY_KEYS.TA_SIGNAL_PRICE_CHART,
      assetId,
      signalTime,
      assetType,
    ],
    enabled:
      !!openDataApi &&
      !!assetId &&
      !!signalTime &&
      enabled &&
      !!startTime &&
      !!endTime,
    queryFn: async (): Promise<TASignalPriceChartData> => {
      if (
        !openDataApi ||
        !assetId ||
        signalTime == null ||
        startTime == null ||
        endTime == null
      ) {
        return { XAxis: [], YData: [] };
      }

      try {
        if (assetType === "stock") {
          const res = await openDataApi.stocks.getStocks({
            limit: 1,
            ids: [assetId],
            "enrich.enable": true,
            "enrich.with_sparkline.enable": true,
            "enrich.with_sparkline.interval": "1h",
            "enrich.with_sparkline.trading_period": "7d",
          });
          const sparkline = (res?.data as any)?.data?.rows?.[0]?.sparkline;
          if (!Array.isArray(sparkline) || sparkline.length <= 1) {
            return { XAxis: [], YData: [] };
          }
          /** 用接口返回的完整 sparkline 排序后再切 24h，避免窄窗口预过滤丢掉与 created 对齐的点 */
          const sorted = [...sparkline].sort(
            (
              a: { timestamp?: number; time?: number },
              b: { timestamp?: number; time?: number },
            ) => (a.timestamp ?? a.time ?? 0) - (b.timestamp ?? b.time ?? 0),
          );
          const filtered = sliceTo24hWindow(
            sorted as { timestamp?: number; time?: number }[],
            signalTime,
          );
          const XAxis: IXAxis = filtered.map(
            (p: { timestamp?: number; time?: number }) => {
              const ts = p.timestamp ?? p.time ?? 0;
              return {
                x: ts,
                label: dayjs(ts).format("MMM DD, HH:mm"),
              };
            },
          );
          const YData = filtered.map((p) =>
            Number((p as { price?: number }).price ?? 0),
          );
          return { XAxis, YData };
        }

        // assetType === "crypto"：加密货币用 getTokens + OHLC
        const res = await openDataApi.tokens.getTokens({
          limit: 1,
          ids: [assetId],
          "enrich.enable": true,
          "enrich.with_ohlc.enable": true,
          "enrich.with_ohlc.start_time": startTime,
          "enrich.with_ohlc.end_time": endTime,
          "enrich.with_ohlc.interval": "1h",
        });
        const ohlc = (res?.data as any)?.data?.rows?.[0]?.ohlc;
        if (!Array.isArray(ohlc) || ohlc.length === 0) {
          return { XAxis: [], YData: [] };
        }
        /** 在接口返回的完整序列上排序再切 24h（请求区间已覆盖展示窗口） */
        const sorted = [...ohlc].sort(
          (a: { time: number }, b: { time: number }) => a.time - b.time,
        );
        const filtered = sliceTo24hWindow(
          sorted as { time: number }[],
          signalTime,
        );
        const XAxis: IXAxis = filtered.map((c: { time: number }) => ({
          x: c.time,
          label: dayjs(c.time).format("MMM DD, HH:mm"),
        }));
        const YData = filtered.map((c) =>
          Number((c as { close?: number }).close ?? 0),
        );
        return { XAxis, YData };
      } catch (err) {
        log.error("[useTASignalPriceChart]", err);
        return { XAxis: [], YData: [] };
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: data ?? { XAxis: [], YData: [] },
    isLoading,
  };
}
